/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useRef, useContext} from 'react'
import {FormattedMessage} from 'react-intl'
import {Box, Stack, Flex, Button, Skeleton, useMultiStyleConfig, SimpleGrid} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'
import OrderSummarySection from './order-summary-section'
import OrderBodySection from './order-body-section'
import {useOrderDetails} from '../../../../custom-hooks/use-order-details'
import {OrderContext, ActionsContext} from '../order-context'

const OrderDetail = () => {
    const styles = useMultiStyleConfig('Orders')
    const {order, isLoading, refetchOrder} = useOrderDetails()

    const {activeReturnShipmentId} = useContext(OrderContext)
    const dispatch = useContext(ActionsContext)

    const headingRef = useRef()
    useEffect(() => {
        // Focus the 'Order Details' header when the component mounts for accessibility
        headingRef?.current?.focus()
    }, [])

    const handleGoBackToDetailClick = () => {
        dispatch({type: 'SET_ACTIVE_SHIPMENT', payload: ''})
    }

    return (
        <SimpleGrid gap={0} data-testid="account-order-details-page">
            {activeReturnShipmentId ? (
                <Button
                    onClick={handleGoBackToDetailClick}
                    variant="link"
                    {...styles.goBackToOrderHistory}
                >
                    <FormattedMessage
                        defaultMessage="Back to Order Detail"
                        id="account_order_detail.link.back_to_detail"
                    />
                </Button>
            ) : (
                <Button
                    as={Link}
                    to={'/account/orders'}
                    variant="link"
                    {...styles.goBackToOrderHistory}
                >
                    <FormattedMessage
                        defaultMessage="Back to Order History"
                        id="account_order_detail.link.back_to_history"
                    />
                </Button>
            )}

            <Box layerStyle="cardBordered">
                {isLoading ? (
                    <>
                        <Stack>
                            <Skeleton h="20px" w="84px" />
                            <Skeleton h="20px" w="112px" />
                            <Skeleton h="20px" w="56px" />
                        </Stack>
                        <Stack>
                            <Skeleton h="20px" w="84px" />
                            <Skeleton h="20px" w="56px" />
                        </Stack>
                        <Stack>
                            <Skeleton h="20px" w="112px" />
                            <Skeleton h="20px" w="84px" />
                            <Skeleton h="20px" w="56px" />
                        </Stack>
                        <Stack>
                            <Skeleton h="20px" w="60px" />
                            <Skeleton h="20px" w="84px" />
                            <Skeleton h="20px" w="56px" />
                        </Stack>
                    </>
                ) : typeof order === 'object' ? (
                    <OrderSummarySection order={order} refetchOrder={refetchOrder} />
                ) : (
                    <></>
                )}
            </Box>

            <Box>
                {isLoading ? (
                    [1, 2, 3].map((i) => (
                        <Box
                            key={i}
                            p={[4, 6]}
                            border="1px solid"
                            borderColor="gray.100"
                            borderRadius="base"
                        >
                            <Flex width="full" align="flex-start">
                                <Skeleton boxSize={['88px', 36]} mr={4} />

                                <Stack spacing={2}>
                                    <Skeleton h="20px" w="112px" />
                                    <Skeleton h="20px" w="84px" />
                                    <Skeleton h="20px" w="140px" />
                                </Stack>
                            </Flex>
                        </Box>
                    ))
                ) : typeof order === 'object' ? (
                    <OrderBodySection order={order} refetchOrder={refetchOrder} />
                ) : (
                    <></>
                )}
            </Box>
        </SimpleGrid>
    )
}

OrderDetail.getTemplateName = () => 'order-detail'

export default OrderDetail
